/* cc-ar.js - 17 October 2026
   "See it on your own wall": builds a framed print as a 3D model IN THE
   BROWSER and hands it to iOS AR Quick Look.

   WHY THERE ARE NO STORED ASSETS. 75 photographs x 4 sizes x 3 frame
   colours is 1,200 combinations. Nobody pre-generates 1,200 models, and
   that is what normally kills AR for a print shop. A framed print is
   geometrically trivial though: two boxes and a textured quad. So the model
   is assembled per tap from the photograph, the size in centimetres and the
   frame colour, and nothing is stored anywhere.

   WHY NO LIBRARY. three.js has a USDZExporter and would work, but it is
   ~600 KB to draw two boxes, and this site has a recorded decision against
   third-party origins. A USDZ is a zip of a text file and a JPEG, and the
   whole writer is below.

   iOS ONLY, DELIBERATELY. Quick Look is the only path implemented. Android
   would need a WebXR session with hit testing, which is a different and
   much larger piece, and Google's Scene Viewer cannot take a blob at all
   because it runs outside the browser sandbox. cc-content's rule is that
   the button is ABSENT where it does not work, so supported() tests for
   Quick Look alone and Android simply never sees it. That is honest; a
   button that opens nothing is not.

   THE USDZ CONTAINER, from the OpenUSD spec:
     - a zip with NO compression (every entry STORED) and no encryption
     - no ZIP64
     - each entry's DATA begins at a multiple of 64 bytes from the start of
       the archive, achieved by padding the local header's extra field
     - the first entry must be a native usd file

   The alignment is the part that fails silently: a package whose data is
   off the boundary can still unzip perfectly and still be rejected. It is
   checked, from the bytes, by scripts/check-usdz.js.

   NOT VERIFIED ON A DEVICE. No simulator and no preview pane has a camera,
   so the container is verified from its bytes and the scene is not verified
   at all. Two things only an iPhone can settle: whether Quick Look accepts
   this hand-written Preliminary_AnchoringAPI, which is a preliminary schema
   with no full public specification, and whether the print faces the room
   rather than the wall once anchored. The button stays switched off until
   Charly has seen it. */
(function () {
  'use strict';

  /* Two sections can each offer AR on the same page, the DIY Room limits
     block and the builder, and each loads this file because neither can
     assume the other is present. Identical script tags are NOT deduplicated
     by the browser, so without this the IIFE would run twice and mount
     every slot twice. */
  if (window.ccAR) return;

  var FRAME_RGB = {
    black:   [0.102, 0.102, 0.094],
    white:   [0.965, 0.961, 0.945],
    natural: [0.788, 0.624, 0.408],
  };
  var FRAME_FACE_CM = 2;   /* a box frame adds 20mm of face on every edge */
  var FRAME_DEPTH_CM = 3;

  /* ------------------------------------------------------------ support */

  function supported() {
    try {
      var a = document.createElement('a');
      return !!(a.relList && a.relList.supports && a.relList.supports('ar'));
    } catch (e) { return false; }
  }

  /* -------------------------------------------------------------- zip */

  function crc32(buf) {
    var t = crc32.t;
    if (!t) {
      t = crc32.t = new Int32Array(256);
      for (var n = 0; n < 256; n++) {
        var c = n;
        for (var k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        t[n] = c;
      }
    }
    var crc = -1;
    for (var i = 0; i < buf.length; i++) crc = (crc >>> 8) ^ t[(crc ^ buf[i]) & 0xFF];
    return (crc ^ -1) >>> 0;
  }

  /* A zip writer that exists for one reason: to put each entry's DATA on a
     64-byte boundary. Everything else is an ordinary stored-entry zip. The
     padding goes in the local header's extra field, which is what usdzip
     itself does and what any unzip tool harmlessly ignores. */
  function zipStored(files) {
    var chunks = [], offset = 0, central = [];

    function push(u8) { chunks.push(u8); offset += u8.length; }
    function u16(v) { return new Uint8Array([v & 255, (v >> 8) & 255]); }
    function u32(v) { return new Uint8Array([v & 255, (v >> 8) & 255, (v >> 16) & 255, (v >>> 24) & 255]); }
    function cat(parts) {
      var n = 0, i;
      for (i = 0; i < parts.length; i++) n += parts[i].length;
      var out = new Uint8Array(n), p = 0;
      for (i = 0; i < parts.length; i++) { out.set(parts[i], p); p += parts[i].length; }
      return out;
    }

    files.forEach(function (f) {
      var name = new TextEncoder().encode(f.name);
      var crc = crc32(f.data);
      var headerLen = 30 + name.length;
      /* how much extra field is needed so DATA lands on 64 */
      var pad = (64 - ((offset + headerLen) % 64)) % 64;
      /* an extra field is a 4-byte header plus payload, so it cannot be 1, 2
         or 3 bytes long. Take another 64 when the gap is too small to fill. */
      if (pad > 0 && pad < 4) pad += 64;
      var extra;
      if (pad === 0) {
        extra = new Uint8Array(0);
      } else {
        extra = new Uint8Array(pad);
        extra.set(u16(0xFFFF), 0);        /* a private, ignorable id */
        extra.set(u16(pad - 4), 2);
      }
      var localOffset = offset;
      push(cat([u32(0x04034b50), u16(10), u16(0), u16(0), u16(0), u16(0),
                u32(crc), u32(f.data.length), u32(f.data.length),
                u16(name.length), u16(extra.length), name, extra]));
      if (offset % 64 !== 0) throw new Error('alignment maths is wrong at ' + f.name + ': data at ' + offset);
      push(f.data);
      central.push(cat([u32(0x02014b50), u16(0x031E), u16(10), u16(0), u16(0), u16(0), u16(0),
                        u32(crc), u32(f.data.length), u32(f.data.length),
                        u16(name.length), u16(0), u16(0), u16(0), u16(0), u32(0),
                        u32(localOffset), name]));
    });

    var cdStart = offset;
    central.forEach(push);
    push(cat([u32(0x06054b50), u16(0), u16(0), u16(central.length), u16(central.length),
              u32(offset - cdStart), u32(cdStart), u16(0)]));
    return cat(chunks);
  }

  /* -------------------------------------------------------------- usda */

  function quad(w, h, z) {
    var x = (w / 2).toFixed(5), y = (h / 2).toFixed(5), zz = z.toFixed(5);
    return '(-' + x + ', -' + y + ', ' + zz + '), (' + x + ', -' + y + ', ' + zz + '), ('
         + x + ', ' + y + ', ' + zz + '), (-' + x + ', ' + y + ', ' + zz + ')';
  }

  function boxPoints(w, h, d) {
    var x = (w / 2).toFixed(5), y = (h / 2).toFixed(5), z = d.toFixed(5);
    return '(-' + x + ', -' + y + ', -' + z + '), (' + x + ', -' + y + ', -' + z + '), ('
         + x + ', ' + y + ', -' + z + '), (-' + x + ', ' + y + ', -' + z + '), '
         + '(-' + x + ', -' + y + ', 0), (' + x + ', -' + y + ', 0), ('
         + x + ', ' + y + ', 0), (-' + x + ', ' + y + ', 0)';
  }

  /* A ROW OF PRINTS, not one. The builder sells two or three hung together
     and that is the thing someone wants against their own wall: one print
     in AR answers a question nobody asked. The layout is the builder's own,
     centred, framed outer sizes, 9cm between, so what appears on the wall
     is what the preview showed. A single print is just a row of one. */
  function usda(o) {
    var fd = FRAME_DEPTH_CM / 100;
    var c = FRAME_RGB[o.frame] || FRAME_RGB.black;
    var col = '(' + c[0] + ', ' + c[1] + ', ' + c[2] + ')';
    var gap = (o.gapCm == null ? 9 : o.gapCm) / 100;

    var items = o.items.map(function (it) {
      var pw = it.widthCm / 100, ph = it.heightCm / 100;
      return { pw: pw, ph: ph,
               fw: pw + (FRAME_FACE_CM * 2) / 100,
               fh: ph + (FRAME_FACE_CM * 2) / 100,
               tex: it.textureName };
    });
    var rowW = items.reduce(function (a, i) { return a + i.fw; }, 0) + gap * (items.length - 1);
    var x = -rowW / 2;
    items.forEach(function (i) { i.cx = x + i.fw / 2; x += i.fw + gap; });

    var prims = items.map(function (i, n) {
      var id = 'P' + n;
      return '    def Xform "' + id + '"\n    {\n'
        + '        float3 xformOp:translate = (' + i.cx.toFixed(5) + ', 0, 0)\n'
        + '        uniform token[] xformOpOrder = ["xformOp:translate"]\n\n'
        + '        def Mesh "Frame"\n        {\n'
        + '            int[] faceVertexCounts = [4, 4, 4, 4, 4, 4]\n'
        + '            int[] faceVertexIndices = [0,1,2,3, 7,6,5,4, 0,4,5,1, 1,5,6,2, 2,6,7,3, 3,7,4,0]\n'
        + '            point3f[] points = [' + boxPoints(i.fw, i.fh, fd) + ']\n'
        + '            uniform token subdivisionScheme = "none"\n'
        + '            rel material:binding = </Root/' + id + '/Frame/M>\n'
        + '            def Material "M"\n            {\n'
        + '                token outputs:surface.connect = </Root/' + id + '/Frame/M/S.outputs:surface>\n'
        + '                def Shader "S"\n                {\n'
        + '                    uniform token info:id = "UsdPreviewSurface"\n'
        + '                    color3f inputs:diffuseColor = ' + col + '\n'
        + '                    float inputs:roughness = 0.65\n'
        + '                    float inputs:metallic = 0\n'
        + '                    token outputs:surface\n'
        + '                }\n            }\n        }\n\n'
        + '        def Mesh "Print"\n        {\n'
        + '            int[] faceVertexCounts = [4]\n'
        + '            int[] faceVertexIndices = [0, 1, 2, 3]\n'
        + '            point3f[] points = [' + quad(i.pw, i.ph, 0.0012) + ']\n'
        + '            texCoord2f[] primvars:st = [(0, 0), (1, 0), (1, 1), (0, 1)] (\n'
        + '                interpolation = "faceVarying"\n            )\n'
        + '            uniform token subdivisionScheme = "none"\n'
        + '            rel material:binding = </Root/' + id + '/Print/M>\n'
        + '            def Material "M"\n            {\n'
        + '                token outputs:surface.connect = </Root/' + id + '/Print/M/S.outputs:surface>\n'
        + '                def Shader "S"\n                {\n'
        + '                    uniform token info:id = "UsdPreviewSurface"\n'
        + '                    color3f inputs:diffuseColor.connect = </Root/' + id + '/Print/M/T.outputs:rgb>\n'
        /* Enhanced Matte Art 200gsm: a matte paper is not shiny */
        + '                    float inputs:roughness = 0.9\n'
        + '                    float inputs:metallic = 0\n'
        + '                    token outputs:surface\n'
        + '                }\n'
        + '                def Shader "T"\n                {\n'
        + '                    uniform token info:id = "UsdUVTexture"\n'
        + '                    asset inputs:file = @' + i.tex + '@\n'
        + '                    float2 inputs:st.connect = </Root/' + id + '/Print/M/P.outputs:result>\n'
        + '                    token inputs:wrapS = "clamp"\n'
        + '                    token inputs:wrapT = "clamp"\n'
        + '                    float3 outputs:rgb\n'
        + '                }\n'
        + '                def Shader "P"\n                {\n'
        + '                    uniform token info:id = "UsdPrimvarReader_float2"\n'
        + '                    token inputs:varname = "st"\n'
        + '                    float2 outputs:result\n'
        + '                }\n'
        + '            }\n        }\n    }\n';
    }).join('\n');

    return '#usda 1.0\n'
      + '(\n    defaultPrim = "Root"\n    metersPerUnit = 1\n    upAxis = "Y"\n)\n\n'
      /* Preliminary_AnchoringAPI is how Quick Look is told this hangs on a
         WALL rather than standing on the floor. It is a preliminary schema
         with no full public specification, known from Reality Composer
         output and WWDC sessions, so it is the single most likely thing to
         need changing after a real device sees it. */
      + 'def Xform "Root" (\n'
      + '    prepend apiSchemas = ["Preliminary_AnchoringAPI"]\n'
      + '    kind = "component"\n'
      + ')\n{\n'
      + '    uniform token preliminary:anchoring:type = "plane"\n'
      + '    uniform token preliminary:planeAnchoring:alignment = "vertical"\n\n'
      + prims
      + '}\n';
  }

  /* ------------------------------------------------------------- build */

  /* The extension has to match the actual bytes. A USDZ may carry png or
     jpeg, but naming a PNG "print.jpg" is a texture that silently fails to
     load, and the source here is whatever Shopify happens to hold: the
     product images on this store are PNG, and only the &format=jpg on the
     request makes them otherwise. Requesting jpg and then checking is
     cheap; trusting the request is how you ship an invisible photograph. */
  function textureExt(bytes) {
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) return '.png';
    if (bytes[0] === 0xFF && bytes[1] === 0xD8) return '.jpg';
    return null;
  }

  function buildUsdz(opts) {
    var files = [], items = [];
    opts.items.forEach(function (it, n) {
      var ext = textureExt(it.texture);
      if (!ext) throw new Error('texture ' + n + ' is neither JPEG nor PNG, and a usdz may carry no other image');
      var name = 'print' + n + ext;
      files.push({ name: name, data: it.texture });
      items.push({ widthCm: it.widthCm, heightCm: it.heightCm, textureName: name });
    });
    var text = new TextEncoder().encode(usda({
      items: items, frame: opts.frame, gapCm: opts.gapCm,
    }));
    /* the first entry must be a native usd file */
    return zipStored([{ name: 'model.usda', data: text }].concat(files));
  }

  /* -------------------------------------------------------------- open */

  function open(cfg) {
    /* one print and a set are the same call: a single print is a row of
       one. Callers that pass imageUrl/widthCm/heightCm still work. */
    var items = cfg.items || [{ imageUrl: cfg.imageUrl, widthCm: cfg.widthCm, heightCm: cfg.heightCm }];
    return Promise.all(items.map(function (it) {
      return fetch(it.imageUrl, { mode: 'cors' }).then(function (r) {
        if (!r.ok) throw new Error('texture ' + r.status);
        return r.arrayBuffer();
      }).then(function (b) {
        return { texture: new Uint8Array(b), widthCm: it.widthCm, heightCm: it.heightCm };
      });
    })).then(function (loaded) {
      var bytes = buildUsdz({ items: loaded, frame: cfg.frame, gapCm: cfg.gapCm });
      var url = URL.createObjectURL(new Blob([bytes], { type: 'model/vnd.usdz+zip' }));
      /* Quick Look is opened by CLICKING AN ANCHOR with rel="ar", and the
         anchor must contain an element (an img) as its first child or it
         is treated as an ordinary download. It cannot be opened by
         assigning location. */
      var a = document.createElement('a');
      a.rel = 'ar';
      a.href = url;
      var img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.style.cssText = 'width:1px;height:1px;opacity:0;position:absolute;pointer-events:none';
      a.appendChild(img);
      a.style.cssText = 'position:absolute;width:1px;height:1px;opacity:0;pointer-events:none';
      document.body.appendChild(a);
      a.click();
      window.setTimeout(function () { a.remove(); URL.revokeObjectURL(url); }, 20000);
      return true;
    });
  }

  /* ------------------------------------------------------------- mount */

  /* THE ENGINE MOUNTS ITSELF, wherever a slot exists. Charly asked for AR
     "home and dedicated page", and the first version had the button-making
     code inside cc-diy-page.js, which only loads on the DIY Room page. Two
     pages wiring up the same feature twice is two places for the absent-
     where-unsupported rule to be got wrong. One definition, here, beside
     the thing that knows whether it works. */
  function mount(slot) {
    if (!slot || slot.getAttribute('data-ar-mounted')) return;
    slot.setAttribute('data-ar-mounted', '1');
    if (supported()) { mountAR(slot); } else { mountQR(slot); }
  }

  function mountAR(slot) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cc-btn cc-btn--primary cc-ar__btn';
    var label = slot.getAttribute('data-label') || 'Point your phone at the wall';
    b.textContent = label;
    b.addEventListener('click', function () {
      if (b.disabled) return;
      b.disabled = true;
      b.textContent = 'Opening\u2026';
      open({
        imageUrl: slot.getAttribute('data-image'),
        widthCm: parseFloat(slot.getAttribute('data-w-cm')) || 60,
        heightCm: parseFloat(slot.getAttribute('data-h-cm')) || 90,
        frame: currentFrame(slot)
      }).catch(function () {
        b.textContent = 'That did not open. Try once more.';
      }).then(function () {
        window.setTimeout(function () { b.disabled = false; b.textContent = label; }, 2500);
      });
    });
    slot.appendChild(wrap(b, slot.getAttribute('data-hint')));
  }

  /* DESKTOP GETS A DOOR, NOT NOTHING. Charly, 17 Oct: the button must be on
     desktop too and invite someone to scan a code and carry on on their
     phone. That replaces the earlier rule of being absent where
     unsupported, and it is better: the feature is real on this page, it
     just needs the device in the visitor's pocket.

     An inline disclosure rather than a modal, deliberately. A dialog would
     want a focus trap, a scroll lock and a z-index, and this site has a
     sticky hero and a fixed frame-switch pill already competing for the
     last of those. A panel that opens under the button has none of those
     problems and is no worse to use.

     If the QR cannot be made, nothing renders at all. That is the one case
     where absent is still right: a broken code is worse than no invitation,
     and it only happens if the address is longer than a version 5 code
     holds, which no url on this site is. */
  function mountQR(slot) {
    if (!window.ccQR) return;
    var url = qrUrl();
    var svg = window.ccQR.svg(url, { px: 168, label: 'QR code linking to this page' });
    if (!svg) return;

    var w = qrPanel(url, slot.getAttribute('data-qr-label'), slot.getAttribute('data-qr-hint'),
                    slot.getAttribute('data-hint'));
    if (w) slot.appendChild(w);
  }

  /* Exposed, because the builder needs one of these with its OWN address
     rather than the page's: the set someone has just made has to travel to
     the phone with them. Returns null if no code can be made, so a caller
     can render nothing rather than something broken. */
  function qrPanel(url, label, qrHint, hint) {
    if (!window.ccQR) return null;
    var svg = window.ccQR.svg(url, { px: 168, label: 'QR code linking to this page' });
    if (!svg) return null;

    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cc-btn cc-btn--primary cc-ar__btn';
    /* "See it on your own wall" on a DESKTOP promises something that will
       not happen there. cc-content's rule moved from presence to wording
       once the button stopped being absent: name the action on the button,
       make the promise on the panel behind it. */
    b.textContent = label || 'Open it on your phone';
    b.setAttribute('aria-expanded', 'false');

    var panel = document.createElement('div');
    panel.className = 'cc-ar__qr';
    panel.hidden = true;
    panel.innerHTML = svg;
    var note = document.createElement('p');
    note.className = 'cc-ar__qrnote';
    note.textContent = qrHint
      || 'Scan this with your phone camera. The print appears on your wall at its real size.';
    panel.appendChild(note);

    var id = 'cc-ar-qr-' + Math.random().toString(36).slice(2, 8);
    panel.id = id;
    b.setAttribute('aria-controls', id);
    b.addEventListener('click', function () {
      var isOpen = panel.hidden;
      panel.hidden = !isOpen;
      b.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    var w = wrap(b, hint);
    w.appendChild(panel);
    return w;
  }

  /* THE CODE MUST REACH THE SAME THEME IT WAS SCANNED FROM. Shopify takes
     preview_theme_id off the address once it has set its preview cookie, so
     location.href on a draft is the LIVE url. A code built from it sends a
     phone to the published site, where whatever is being previewed does not
     exist yet. Caught while checking the draft: the panel's own code decoded
     to the live homepage.

     The page knows which theme it is, so put the parameter back when it is
     not the live one. On the live theme nothing is appended and the address
     stays clean, which is what a customer should be scanning. */
  function qrUrl() {
    var url = location.href;
    try {
      var t = window.Shopify && window.Shopify.theme;
      if (t && t.role && t.role !== 'main' && t.id && url.indexOf('preview_theme_id=') === -1) {
        url += (url.indexOf('?') > -1 ? '&' : '?') + 'preview_theme_id=' + t.id;
      }
    } catch (e) { /* a missing Shopify global just means the plain address */ }
    return url;
  }

  function wrap(button, hintText) {
    var w = document.createElement('div');
    w.className = 'cc-ar';
    w.appendChild(button);
    if (hintText) {
      var hint = document.createElement('p');
      hint.className = 'cc-ar__hint';
      hint.textContent = hintText;
      w.appendChild(hint);
    }
    return w;
  }

  /* The frame colour can be fixed, or read from a live control at the
     moment of the tap. The homepage band already lets someone change the
     frame in pure CSS, so AR should show the colour they are looking at
     rather than a default they did not choose. */
  function currentFrame(slot) {
    var sel = slot.getAttribute('data-frame-radio');
    if (sel) {
      var hit = document.querySelector('input[name="' + sel + '"]:checked');
      if (hit && FRAME_RGB[hit.value]) return hit.value;
    }
    var fixed = slot.getAttribute('data-frame');
    return FRAME_RGB[fixed] ? fixed : 'black';
  }

  function mountAll() {
    var slots = document.querySelectorAll('[data-cc-ar]');
    for (var i = 0; i < slots.length; i++) mount(slots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll, { once: true });
  } else {
    mountAll();
  }

  window.ccAR = {
    supported: supported, open: open, mount: mount, mountAll: mountAll,
    qrPanel: qrPanel, qrUrl: qrUrl,
    _buildUsdz: buildUsdz, _usda: usda, _zip: zipStored,
  };
}());
